// ============================================================
// FirmSignatureMoveCard.tsx — B3b: the one-time "Move the existing firm
// signature to a user" helper (Admin › Billing). Signatures now belong to
// each person (SignatureUploadCard); the old firm-wide image is no longer
// printed. Shown only while the firm still has one: the admin picks whose it
// is, the server copies it to that user and clears the firm's copy (audited
// as firm.invoice_signature_moved), and this card disappears. Never matched
// by name — the admin chooses.
// ============================================================
import React, { useEffect, useState } from 'react'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import { SettingsCard, S_BTN_PRIMARY, S_NOTE, S_FORMFOOT, sInput } from './settingsUi'
import { getFirmSignatureApi, listUsersApi, moveFirmSignatureApi } from '../../api/casevoApi'
import type { FirmSignature } from '../../types/billing.types'
import { serverErrorMessage } from '../../utils/serverError'
import { navigateTo } from '../../navigation/navigate'

// B3c — Admin › Billing's one line about signatures, both places as links.
export const SignaturePerPersonNote: React.FC<{ onOpenTeam?: () => void }> = ({ onOpenTeam }) => (
  <p className={`${S_NOTE} mt-[2px]`}>Signatures are now set per person:{' '}
    <button type="button" className="underline hover:no-underline focus:outline-none casevo-focus rounded-sm" onClick={() => { void navigateTo({ view: 'settings', params: { section: 'account' } }) }}>My settings</button>
    {' '}(your own) or{' '}
    <button type="button" className="underline hover:no-underline focus:outline-none casevo-focus rounded-sm" onClick={onOpenTeam}>Admin › Team</button>
    {' '}(anyone’s).</p>
)

interface Member { id: string; firstName: string; lastName: string; isActive: boolean }

const FirmSignatureMoveCard: React.FC = () => {
  const [sig, setSig] = useState<FirmSignature | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [userId, setUserId] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    getFirmSignatureApi().then((s) => {
      if (!live) return
      setSig(s)
      if (s.present) listUsersApi().then((u) => { if (live) setMembers((u as Member[]).filter((m) => m.isActive)) }).catch(() => undefined)
    }).catch(() => undefined) // nothing to move if it can't be read — the card stays hidden
    return () => { live = false }
  }, [])

  if (done) return <p role="status" className={`${S_NOTE} m-0`}>{done}</p>
  if (!sig?.present) return null
  const move = async (): Promise<void> => {
    if (!userId) { setErr('Choose whose signature this is.'); return }
    setBusy(true); setErr(null)
    try {
      await moveFirmSignatureApi(userId)
      const m = members.find((x) => x.id === userId)
      setDone(`The signature now belongs to ${m ? `${m.firstName} ${m.lastName}`.trim() : 'that person'}. It prints on bills they sign.`)
    } catch (e) { setErr(serverErrorMessage(e) ?? 'Couldn’t move the signature.') } finally { setBusy(false) }
  }
  return (
    <SettingsCard icon="pen" title="Move the existing firm signature to a user" sub="Signatures now belong to each person and print on the bills they sign. Your firm’s older, firm-wide signature image is no longer printed. Choose whose signature it is to move it to them.">
      <div className="flex flex-wrap items-start gap-[14px] pt-[6px]">
        <div className="w-[220px] h-[72px] rounded-[10px] border border-casevo-border bg-white grid place-items-center overflow-hidden shrink-0">
          {sig.signatureUrl ? <img src={sig.signatureUrl} alt="The firm signature" className="max-w-[200px] max-h-[60px] object-contain" /> : null}
        </div>
        <div className="min-w-[240px] flex-1 max-w-[340px]">
          <FormField label="Whose signature is this?" required variant="info" htmlFor="firm-sig-owner">
            <select id="firm-sig-owner" className={sInput(!!err)} value={userId} disabled={busy} onChange={(e) => { setUserId(e.target.value); setErr(null) }}
              aria-invalid={!!err || undefined} aria-describedby={err ? 'firm-sig-owner-err' : undefined}>
              <option value="">Choose a person…</option>
              {members.map((m) => <option key={m.id} value={m.id}>{`${m.firstName} ${m.lastName}`.trim()}</option>)}
            </select>
            <span id="firm-sig-owner-err"><FieldError message={err ?? undefined} /></span>
          </FormField>
          {sig.name && <p className={`${S_NOTE} mt-[4px] mb-0`}>It was set up for “{sig.name}{sig.title ? `, ${sig.title}` : ''}”.</p>}
        </div>
      </div>
      <div className={S_FORMFOOT}>
        <button type="button" className={S_BTN_PRIMARY} disabled={busy} onClick={() => { void move() }}>{busy ? 'Moving…' : 'Move signature'}</button>
      </div>
    </SettingsCard>
  )
}

export default FirmSignatureMoveCard
